import { useEffect, useState, useMemo } from "react";
import api, { errMsg, formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import ExportButtons from "@/components/app/ExportButtons";
import { toast } from "sonner";
import { Wallet, TrendingUp, TrendingDown, Truck, Building2, Factory, Calendar } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
  ComposedChart, Line
} from "recharts";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

export default function FinancialReportsPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/financial?year=${y}`);
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(year); }, [year]);

  const monthly = useMemo(() => (data?.monthly || []).map(m => ({ ...m, label: monthLabel(m.month) })), [data]);
  const yearly = data?.yearly || {};
  const perVehicle = data?.per_vehicle || [];
  const perCompany = data?.per_company || [];
  const perSupplier = data?.per_supplier || [];

  return (
    <>
      <PageHeader
        title="Kapsamlı Finansal Rapor"
        subtitle="Aylık ve yıllık kazanç, tedarikçi ve firma faturaları, araç bazlı kâr/zarar, firma ve tedarikçi kırılımları."
        testid="financial-reports-header"
        actions={
          <div className="flex items-center gap-2">
            <ExportButtons
              filename={`kapsamli_finansal_rapor_${year}`}
              title={`Kapsamlı Finansal Rapor — ${year}`}
              subtitle={`Toplam Gelir: ${formatTL(yearly.total_income || 0)} · Toplam Gider: ${formatTL(yearly.total_expense || 0)} · Net: ${formatTL(yearly.net || 0)}`}
              orientation="landscape"
              sections={[
                {
                  name: "Aylık Kırılım", heading: "Aylık Kırılım",
                  columns: [
                    { key: "label", label: "Ay" },
                    { key: "vehicle_earnings", label: "Araç Kazancı", format: "money" },
                    { key: "sales_invoices", label: "Kesilen Fatura", format: "money" },
                    { key: "incoming_payments", label: "Tahsilat", format: "money" },
                    { key: "total_income", label: "Toplam Gelir", format: "money" },
                    { key: "supplier_invoices", label: "Tedarikçi Faturası", format: "money" },
                    { key: "fuel", label: "Yakıt", format: "money" },
                    { key: "salaries", label: "Maaş", format: "money" },
                    { key: "total_expense", label: "Toplam Gider", format: "money" },
                    { key: "net", label: "Net", format: "money" },
                  ],
                  rows: monthly,
                },
                {
                  name: "Araç Bazlı", heading: "Araç Bazlı Kar/Zarar",
                  columns: [
                    { key: "plate", label: "Plaka" },
                    { key: "model", label: "Model" },
                    { key: "total_income", label: "Toplam Gelir", format: "money" },
                    { key: "fuel_expense", label: "Yakıt Gideri", format: "money" },
                    { key: "total_km", label: "Toplam Km", format: "number" },
                    { key: "tl_per_km", label: "₺/Km", format: "money" },
                    { key: "net_profit", label: "Net Kar", format: "money" },
                  ],
                  rows: perVehicle,
                },
                {
                  name: "Firma Bazlı", heading: "Firma Bazlı Kazanç",
                  columns: [
                    { key: "company_name", label: "Firma" },
                    { key: "sales_invoices", label: "Kesilen Fatura", format: "money" },
                    { key: "incoming_payments", label: "Tahsilat", format: "money" },
                    { key: "total_revenue", label: "Toplam Ciro", format: "money" },
                  ],
                  rows: perCompany,
                },
                {
                  name: "Tedarikçi", heading: "Tedarikçi Bazlı Harcama",
                  columns: [
                    { key: "supplier", label: "Tedarikçi" },
                    { key: "invoiced", label: "Kesilen Fatura", format: "money" },
                    { key: "paid", label: "Ödenen", format: "money" },
                  ],
                  rows: perSupplier,
                },
              ]}
            />
            <Calendar className="w-4 h-4 text-neutral-400 ml-2" />
            <select data-testid="year-select" value={year} onChange={e => setYear(Number(e.target.value))} className="input" style={{ width: 120 }}>
              {(data?.years_available?.length ? data.years_available : [year]).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }
      />

      {loading && <div className="text-neutral-500 text-sm py-4">Yükleniyor...</div>}
      {!loading && (
        <>
          {/* Yearly KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPICard testid="yr-income" label={`${year} Toplam Gelir`} value={formatTL(yearly.total_income)}
                     sub={`Fatura + Araç + Tahsilat`} accent="success" icon={TrendingUp} />
            <KPICard testid="yr-expense" label={`${year} Toplam Gider`} value={formatTL(yearly.total_expense)}
                     sub="Fatura + Yakıt + Maaş" accent="error" icon={TrendingDown} />
            <KPICard testid="yr-net" label={`${year} Net Kar/Zarar`} value={formatTL(yearly.net)}
                     sub={yearly.net >= 0 ? "Kar" : "Zarar"} accent={yearly.net >= 0 ? "success" : "error"} icon={Wallet} />
            <KPICard testid="yr-fuel" label={`${year} Toplam Yakıt`} value={formatTL(yearly.fuel)}
                     sub={`Maaş: ${formatTL(yearly.salaries)}`} accent="warning" />
          </div>

          {/* Detailed yearly breakdown */}
          <div className="bg-white border border-[#E5E5E5] rounded-md p-5 mb-6" data-testid="yearly-detail">
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-4">Yıllık Kırılım — {year}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <BreakdownGroup title="Gelirler" total={yearly.total_income} color="#16A34A" items={[
                { label: "Araç Kazançları", value: yearly.vehicle_earnings },
                { label: "Kesilen Faturalar", value: yearly.sales_invoices },
                { label: "Gelen Ödemeler", value: yearly.incoming_payments },
              ]} />
              <BreakdownGroup title="Tedarikçi" total={yearly.supplier_invoices} color="#F59E0B" items={[
                { label: "Kesilen Faturalar", value: yearly.supplier_invoices },
                { label: "Yapılan Ödemeler", value: yearly.supplier_payments },
              ]} />
              <BreakdownGroup title="Operasyon" total={yearly.fuel + yearly.salaries} color="#DC2626" items={[
                { label: "Yakıt", value: yearly.fuel },
                { label: "Maaşlar", value: yearly.salaries },
              ]} />
              <BreakdownGroup title="Sonuç" total={yearly.net} color={yearly.net >= 0 ? "#16A34A" : "#DC2626"} items={[
                { label: "Toplam Gelir", value: yearly.total_income },
                { label: "Toplam Gider", value: yearly.total_expense, negative: true },
              ]} />
            </div>
          </div>

          {/* Monthly aggregate chart */}
          <div className="bg-white border border-[#E5E5E5] rounded-md p-5 mb-6" data-testid="monthly-chart">
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Aylık Nakit Akışı</div>
            <div className="text-lg font-bold font-heading mt-1 mb-4">Gelir Kırılımı & Net Kar</div>
            {monthly.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-sm text-neutral-500">Bu yıl için veri bulunamadı</div>
            ) : (
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer>
                  <ComposedChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                    <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="vehicle_earnings" name="Araç Kazancı" stackId="in" fill="#0020C2" />
                    <Bar dataKey="sales_invoices" name="Kesilen Fatura" stackId="in" fill="#16A34A" />
                    <Bar dataKey="incoming_payments" name="Tahsilat" stackId="in" fill="#0EA5E9" />
                    <Bar dataKey="supplier_invoices" name="Tedarikçi Fatura" stackId="out" fill="#F59E0B" />
                    <Bar dataKey="fuel" name="Yakıt" stackId="out" fill="#DC2626" />
                    <Bar dataKey="salaries" name="Maaş" stackId="out" fill="#8B5CF6" />
                    <Line type="monotone" dataKey="net" name="Net Kar/Zarar" stroke="#0A0A0A" strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Monthly detail table */}
          <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden mb-6" data-testid="monthly-table">
            <div className="px-5 py-4 border-b border-[#E5E5E5]">
              <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Ay Ay Detay</div>
              <div className="text-lg font-bold font-heading mt-1">Kalem Bazlı Aylık Kırılım</div>
            </div>
            <div className="overflow-x-auto data-table">
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr>
                    {["Ay","Araç Kazancı","Kesilen Fat.","Tahsilat","Toplam Gelir","Tedarikçi Fat.","Yakıt","Maaş","Toplam Gider","Net"].map((h, i) => (
                      <th key={h} className={`px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.length === 0 ? (
                    <tr><td colSpan={10} className="p-8 text-center text-neutral-500">Kayıt yok</td></tr>
                  ) : monthly.slice().reverse().map(m => (
                    <tr key={m.month} className="border-b border-[#F5F5F5]">
                      <td className="px-3 py-3 font-medium">{m.label}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(m.vehicle_earnings)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(m.sales_invoices)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(m.incoming_payments)}</td>
                      <td className="px-3 py-3 text-right num text-[#16A34A] font-semibold">{formatTL(m.total_income)}</td>
                      <td className="px-3 py-3 text-right num text-[#F59E0B]">{formatTL(m.supplier_invoices)}</td>
                      <td className="px-3 py-3 text-right num text-[#DC2626]">{formatTL(m.fuel)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(m.salaries)}</td>
                      <td className="px-3 py-3 text-right num text-[#DC2626] font-semibold">{formatTL(m.total_expense)}</td>
                      <td className={`px-3 py-3 text-right num font-semibold ${m.net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(m.net)}</td>
                    </tr>
                  ))}
                  {monthly.length > 0 && (
                    <tr className="bg-[#FAFAFA] border-t-2 border-[#0A0A0A] font-semibold">
                      <td className="px-3 py-3">TOPLAM</td>
                      <td className="px-3 py-3 text-right num">{formatTL(yearly.vehicle_earnings)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(yearly.sales_invoices)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(yearly.incoming_payments)}</td>
                      <td className="px-3 py-3 text-right num text-[#16A34A]">{formatTL(yearly.total_income)}</td>
                      <td className="px-3 py-3 text-right num text-[#F59E0B]">{formatTL(yearly.supplier_invoices)}</td>
                      <td className="px-3 py-3 text-right num text-[#DC2626]">{formatTL(yearly.fuel)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(yearly.salaries)}</td>
                      <td className="px-3 py-3 text-right num text-[#DC2626]">{formatTL(yearly.total_expense)}</td>
                      <td className={`px-3 py-3 text-right num ${yearly.net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(yearly.net)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-vehicle P&L */}
          <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden mb-6" data-testid="per-vehicle-table">
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
              <Truck className="w-4 h-4 text-neutral-500" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Araç Bazlı Kar/Zarar</div>
                <div className="text-lg font-bold font-heading mt-1">Kazanç − Yakıt Gideri</div>
              </div>
            </div>
            <div className="overflow-x-auto data-table">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr>
                    {["Plaka","Kazanç","Kesilen Fat.","Toplam Gelir","Yakıt","Km","₺/Km","Net Kar"].map((h, i) => (
                      <th key={h} className={`px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perVehicle.length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-neutral-500">Araç verisi yok</td></tr>
                  ) : perVehicle.map(v => (
                    <tr key={v.vehicle_id} className="border-b border-[#F5F5F5]">
                      <td className="px-3 py-3">
                        <div className="num font-semibold">{v.plate}</div>
                        <div className="text-[10px] text-neutral-500">{v.model || "-"}</div>
                      </td>
                      <td className="px-3 py-3 text-right num">{formatTL(v.earnings)}</td>
                      <td className="px-3 py-3 text-right num">{formatTL(v.sales_invoices)}</td>
                      <td className="px-3 py-3 text-right num text-[#16A34A] font-semibold">{formatTL(v.total_income)}</td>
                      <td className="px-3 py-3 text-right num text-[#DC2626]">{formatTL(v.fuel_expense)}</td>
                      <td className="px-3 py-3 text-right num">{formatNumber(v.total_km)}</td>
                      <td className="px-3 py-3 text-right num text-neutral-600">{formatTL(v.tl_per_km)}</td>
                      <td className={`px-3 py-3 text-right num font-semibold ${v.net_profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(v.net_profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-company + per-supplier side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="per-company-table">
              <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
                <Building2 className="w-4 h-4 text-neutral-500" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Firma Bazlı Kazanç</div>
                  <div className="text-lg font-bold font-heading mt-1">Firmalardan Elde Edilen Ciro</div>
                </div>
              </div>
              <div className="overflow-x-auto data-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      {["Firma","Kesilen Fatura","Tahsilat","Toplam"].map((h, i) => (
                        <th key={h} className={`px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perCompany.length === 0 ? (
                      <tr><td colSpan={4} className="p-8 text-center text-neutral-500">Firma verisi yok</td></tr>
                    ) : perCompany.map(c => (
                      <tr key={c.company_name} className="border-b border-[#F5F5F5]">
                        <td className="px-3 py-3 font-medium">{c.company_name}</td>
                        <td className="px-3 py-3 text-right num">{formatTL(c.sales_invoices)}</td>
                        <td className="px-3 py-3 text-right num">{formatTL(c.incoming_payments)}</td>
                        <td className="px-3 py-3 text-right num text-[#16A34A] font-semibold">{formatTL(c.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="per-supplier-table">
              <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
                <Factory className="w-4 h-4 text-neutral-500" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Tedarikçi Bazlı Harcama</div>
                  <div className="text-lg font-bold font-heading mt-1">Tedarikçi Faturaları ve Ödemeleri</div>
                </div>
              </div>
              <div className="overflow-x-auto data-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      {["Tedarikçi","Kesilen Fatura","Ödenen"].map((h, i) => (
                        <th key={h} className={`px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perSupplier.length === 0 ? (
                      <tr><td colSpan={3} className="p-8 text-center text-neutral-500">Tedarikçi verisi yok</td></tr>
                    ) : perSupplier.map(s => (
                      <tr key={s.supplier} className="border-b border-[#F5F5F5]">
                        <td className="px-3 py-3 font-medium">{s.supplier}</td>
                        <td className="px-3 py-3 text-right num text-[#F59E0B] font-semibold">{formatTL(s.invoiced)}</td>
                        <td className="px-3 py-3 text-right num">{formatTL(s.paid)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function BreakdownGroup({ title, total, color, items }) {
  return (
    <div className="border border-[#F5F5F5] p-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">{title}</div>
      <div className="num font-bold text-xl mb-2" style={{ color }}>{formatTL(total)}</div>
      <div className="space-y-1 pt-2 border-t border-[#F5F5F5]">
        {items.map((it) => (
          <div key={it.label} className="flex items-center justify-between text-xs">
            <span className="text-neutral-600">{it.label}</span>
            <span className="num font-medium">{it.negative ? "−" : ""}{formatTL(it.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
